import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ClipboardCheck, MessageSquarePlus, RefreshCw, RotateCcw, ThumbsUp } from 'lucide-react';
import { api, ApiError, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { useBaseCurrency } from '@/hooks/useOrgSettings';
import { usePermissions } from '@/hooks/usePermissions';
import { Attachments } from '@/features/common/Attachments';
import { CustomerSheet } from '@/features/customers/CustomerSheet';
import { RecordPaymentDrawer } from '@/features/payments/RecordPaymentDrawer';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { PageHeader } from '@/app/DashboardLayout';
import { formatDate, formatDateTime, lastMonth, type Period } from '@/lib/dates';
import {
  Badge, Button, Card, EmptyState, ErrorState, Input, Money, PeriodPicker, Spinner, Textarea,
  Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader,
} from '@/features/common/ui';

type Severity = 'high' | 'medium' | 'low';

interface FindingAction {
  kind: 'open' | 'edit-customer' | 'edit-invoice' | 'void-invoice' | 'attach-receipt' | 'record-payment' | 'write-off' | 'categorise' | 'apply-hsn' | 'open-lock';
  label: string;
  href?: string;
  entityId?: string;
}

interface SetAsideNote {
  id: string;
  reason: string;
  byName: string | null;
  at: string;
  stale: boolean;
}

interface Finding {
  checkId: string;
  severity: Severity;
  /** `exportMonth` is a month's EDF; its id is the month itself, "2026-10". */
  entityType: 'invoice' | 'customer' | 'expense' | 'bankTransaction' | 'bankAccount' | 'item' | 'series' | 'exportMonth';
  entityId: string;
  fingerprint?: string;
  label: string;
  detail: string;
  amount?: string;
  date?: string;
  /** What can be done about it, from the server, so the agent reads the same list. */
  actions?: FindingAction[];
  setAside?: SetAsideNote;
  canSetAside?: boolean;
}

/** What a finding's button opens on this page, rather than navigates to. */
type Opened =
  | { kind: 'set-aside'; finding: Finding }
  | { kind: 'attach-receipt'; finding: Finding }
  | { kind: 'edit-customer'; finding: Finding }
  | { kind: 'record-payment'; finding: Finding };

interface ReviewCheck {
  id: string;
  title: string;
  severity: Severity;
  why: string;
  group: 'GST' | 'Exports' | 'Documents' | 'Books';
}

interface ReviewResult {
  period: { from: string; to: string };
  findings: Finding[];
  /** Explained, with the reason - still computed, out of the counts. */
  setAside?: Finding[];
  summary: { high: number; medium: number; low: number; total: number };
  passed: string[];
  skipped: Array<{ checkId: string; reason: string }>;
}

interface ReviewQuery {
  id: string;
  entityType: string;
  entityId: string | null;
  question: string;
  answer: string | null;
  status: 'open' | 'answered' | 'resolved';
  createdAt: string;
  check: ReviewCheck | null;
}

const SEVERITY_LABEL: Record<Severity, string> = { high: 'Fix before filing', medium: 'Worth checking', low: 'Worth a look' };
const SEVERITY_BADGE: Record<Severity, 'danger' | 'warning' | 'neutral'> = {
  high: 'danger',
  medium: 'warning',
  low: 'neutral',
};

/**
 * The review.
 *
 * ── WHAT THIS PAGE IS FOR ──
 * A CA finds a missing HSN or a wrong place of supply by scrolling. Every one
 * of those is a query we can run, and this is where the answers land - grouped
 * by the check that found them, each one linking straight to the record and
 * carrying wording that can be forwarded to the client unedited.
 *
 * ── PASSED AND SKIPPED ARE SHOWN, NOT HIDDEN ──
 * "Fourteen checks ran and found nothing" and "the GST checks did not run" look
 * identical if you only render findings. The second is not a clean bill of
 * health, and a reviewer who cannot tell them apart will read it as one.
 */
export function ReviewPage() {
  const org = useActiveOrg();
  const [period, setPeriod] = useState<Period>(() => lastMonth());
  const [severity, setSeverity] = useState<'all' | Severity | 'aside'>('all');
  const [queryFor, setQueryFor] = useState<Finding | null>(null);
  const [opened, setOpened] = useState<Opened | null>(null);
  const navigate = useNavigate();
  const perms = usePermissions();
  const queryClient = useQueryClient();
  const refreshReview = () => {
    void queryClient.invalidateQueries({ queryKey: ['review', org?.id] });
  };
  const reopen = useMutation({
    mutationFn: (setAsideId: string) => api.delete(`/review/set-aside/${setAsideId}`),
    onSuccess: refreshReview,
  });
  /**
   * ── WHAT A BUTTON DOES ──
   * A fix that has a sheet opens it here, over the list, so the finding is
   * still in view when the sheet closes; a fix that is a page navigates.
   * Both leave the review to recompute itself on the next run.
   */
  const act = (finding: Finding, action: FindingAction) => {
    switch (action.kind) {
      case 'attach-receipt':
      case 'edit-customer':
      case 'record-payment':
        setOpened({ kind: action.kind, finding });
        return;
      case 'apply-hsn':
        navigate('/items?hsn=apply');
        return;
      default:
        if (action.href) navigate(action.href);
    }
  };

  const checks = useQuery({
    queryKey: ['review-checks'],
    queryFn: () => api.get<{ items: ReviewCheck[] }>('/review/checks'),
    staleTime: Infinity,
  });

  const review = useQuery({
    queryKey: ['review', org?.id, period.from, period.to],
    queryFn: () => api.get<ReviewResult>(`/review${qs({ from: period.from, to: period.to })}`),
    enabled: !!org?.id,
  });

  const queries = useQuery({
    queryKey: ['review-queries', org?.id],
    queryFn: () => api.get<{ items: ReviewQuery[] }>('/review/queries?status=all&limit=100'),
    enabled: !!org?.id,
  });

  const checkById = useMemo(
    () => new Map((checks.data?.items ?? []).map((c) => [c.id, c])),
    [checks.data],
  );

  // Grouped by check, so a CA reads "eleven invoices with no HSN" rather than
  // eleven separate rows saying the same thing.
  //
  // Memoised on the QUERY DATA and the filter, not on an intermediate array:
  // a `visible` computed inline is a new array on every render, so a memo
  // keyed on it would recompute every time and cost more than it saved.
  const groups = useMemo(() => {
    const byCheck = new Map<string, Finding[]>();
    const source = severity === 'aside' ? (review.data?.setAside ?? []) : (review.data?.findings ?? []);
    for (const finding of source) {
      if (severity !== 'all' && severity !== 'aside' && finding.severity !== severity) continue;
      const bucket = byCheck.get(finding.checkId);
      if (bucket) bucket.push(finding);
      else byCheck.set(finding.checkId, [finding]);
    }
    return [...byCheck.entries()];
  }, [review.data, severity]);
  const asideCount = review.data?.setAside?.length ?? 0;

  const summary = review.data?.summary;
  const openQueries = (queries.data?.items ?? []).filter((q) => q.status !== 'resolved');

  return (
    <>
      <PageHeader
        title="Review"
        subtitle="What to look at before this period is filed."
        actions={
          <Button size="sm" variant="outline" onClick={() => review.refetch()} disabled={review.isFetching}>
            <RefreshCw className={review.isFetching ? 'size-4 animate-spin' : 'size-4'} aria-hidden />
            Run again
          </Button>
        }
      />

      <div className="space-y-4 px-5 pb-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <PeriodPicker value={period} onChange={setPeriod} />
          <div className="flex flex-wrap gap-1">
            {(['all', 'high', 'medium', 'low', 'aside'] as const).map((level) => (
              <button
                key={level}
                type="button"
                onClick={() => setSeverity(level)}
                aria-pressed={severity === level}
                className={
                  severity === level
                    ? 'rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary touch:min-h-11'
                    : 'rounded-full px-3 py-1 text-xs text-muted-foreground hover:bg-accent touch:min-h-11'
                }
                data-testid={level === 'aside' ? 'review-aside-tab' : undefined}
              >
                {level === 'all' ? 'Everything' : level === 'aside' ? 'Set aside' : SEVERITY_LABEL[level]}
                {summary && level !== 'all' && level !== 'aside' ? ` (${summary[level]})` : ''}
                {level === 'aside' ? ` (${asideCount})` : ''}
              </button>
            ))}
          </div>
        </div>

        {review.isError ? (
          <Card>
            <ErrorState error={review.error} onRetry={() => review.refetch()} />
          </Card>
        ) : review.isLoading ? (
          <Card className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
            <Spinner />
            Running the checks over {formatDate(period.from)} to {formatDate(period.to)}
          </Card>
        ) : (
          <>
            {openQueries.length ? (
              <Card className="p-4">
                <h2 className="text-sm font-semibold">Open queries</h2>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Questions raised against this client&rsquo;s records.
                </p>
                <ul className="mt-3 space-y-2">
                  {openQueries.map((query) => (
                    <QueryRow key={query.id} query={query} orgId={org?.id} />
                  ))}
                </ul>
              </Card>
            ) : null}

            {groups.length === 0 ? (
              <Card>
                <EmptyState
                  icon={CheckCircle2}
                  title={
                    severity === 'all'
                      ? 'Nothing to fix in this period'
                      : severity === 'aside'
                        ? 'Nothing set aside'
                        : `Nothing at "${SEVERITY_LABEL[severity as Severity]}"`
                  }
                  description={
                    severity === 'all'
                      ? `${review.data?.passed.length ?? 0} checks ran and found nothing.`
                      : severity === 'aside'
                        ? 'A finding that is right but explained can be set aside with a reason; it comes back if the record changes.'
                        : 'Other severities may still have findings - switch the filter above.'
                  }
                />
              </Card>
            ) : (
              groups.map(([checkId, findings]) => (
                <FindingGroup
                  key={checkId}
                  check={checkById.get(checkId)}
                  checkId={checkId}
                  findings={findings}
                  aside={severity === 'aside'}
                  canManage={perms.can('review', 'manage')}
                  onRaiseQuery={setQueryFor}
                  onAct={act}
                  onSetAside={(finding) => setOpened({ kind: 'set-aside', finding })}
                  onReopen={(finding) => {
                    if (finding.setAside) reopen.mutate(finding.setAside.id);
                  }}
                />
              ))
            )}

            <RanAndSkipped result={review.data} checkById={checkById} />
          </>
        )}
      </div>

      {queryFor ? (
        <RaiseQueryDrawer
          finding={queryFor}
          check={checkById.get(queryFor.checkId)}
          onClose={() => setQueryFor(null)}
        />
      ) : null}
      {opened?.kind === 'set-aside' ? (
        <SetAsideSheet
          finding={opened.finding}
          check={checkById.get(opened.finding.checkId)}
          onClose={() => setOpened(null)}
          onDone={() => {
            setOpened(null);
            refreshReview();
          }}
        />
      ) : null}
      {opened?.kind === 'attach-receipt' ? (
        <Sheet label="Attach the receipt" onClose={() => { setOpened(null); refreshReview(); }}>
          <SheetHeader title="Attach the receipt" onClose={() => { setOpened(null); refreshReview(); }} />
          <SheetBody>
            <p className="text-sm text-muted-foreground">
              {opened.finding.label}
              {opened.finding.date ? ` · ${formatDate(opened.finding.date)}` : ''}. The finding clears on the next run once a file is on the expense.
            </p>
            <Attachments ownerType="expense" ownerId={opened.finding.entityId} title="Receipt" emptyHint="Nothing attached yet." />
          </SheetBody>
        </Sheet>
      ) : null}
      {opened?.kind === 'edit-customer' ? (
        <CustomerSheet customerId={opened.finding.entityId} onClose={() => setOpened(null)} onSaved={() => { setOpened(null); refreshReview(); }} />
      ) : null}
      {opened?.kind === 'record-payment' ? (
        <RecordPaymentFromFinding invoiceId={opened.finding.entityId} onClose={() => setOpened(null)} onRecorded={() => { setOpened(null); refreshReview(); }} />
      ) : null}
    </>
  );
}

/** The payment drawer needs the invoice's customer and balance; a finding carries only its id. */
function RecordPaymentFromFinding({ invoiceId, onClose, onRecorded }: { invoiceId: string; onClose: () => void; onRecorded: () => void }) {
  const invoice = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => api.get<{ id: string; partyId: string; balanceDue: string; currency: string }>(`/invoices/${invoiceId}`),
  });
  if (!invoice.data) return null;
  return (
    <RecordPaymentDrawer
      onClose={onClose}
      onRecorded={onRecorded}
      prefill={{ customerId: invoice.data.partyId, invoiceId: invoice.data.id, balanceDue: invoice.data.balanceDue, currency: invoice.data.currency }}
    />
  );
}

/**
 * "It's fine, because…" - the reason is what the next person to review the
 * books reads, so it is required and it is kept with the record's
 * fingerprint: change the record and the finding is back, note and all.
 */
function SetAsideSheet({ finding, check, onClose, onDone }: { finding: Finding; check: ReviewCheck | undefined; onClose: () => void; onDone: () => void }) {
  useEscapeToClose(onClose);
  const [reason, setReason] = useState('');
  const mutation = useMutation({
    mutationFn: () =>
      api.post('/review/set-aside', {
        checkId: finding.checkId,
        entityType: finding.entityType,
        entityId: finding.entityId,
        reason: reason.trim(),
        fingerprint: finding.fingerprint,
      }),
    onSuccess: onDone,
  });
  return (
    <Sheet label="Set aside" onClose={onClose}>
      <SheetHeader title="Set this finding aside" onClose={onClose} />
      <SheetForm
        onSubmit={(event) => {
          event.preventDefault();
          if (reason.trim().length >= 3) mutation.mutate();
        }}
      >
        <SheetBody>
          <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">{check?.title ?? finding.checkId}</p>
            <p className="mt-1">
              {finding.label}
              {finding.date ? ` · ${formatDate(finding.date)}` : ''}
            </p>
            <p className="mt-1">{finding.detail}</p>
          </div>
          <Textarea
            label="Why it is fine"
            required
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="e.g. Two orders on the same day, both delivered - not a duplicate."
            error={mutation.error instanceof ApiError ? mutation.error.fieldError('reason') : undefined}
            data-testid="set-aside-reason"
          />
          <p className="text-xs text-muted-foreground">
            Kept with the record and shown to whoever reviews these books next. If the record changes later, the finding comes back with this note beside it.
          </p>
          {mutation.isError && !(mutation.error instanceof ApiError && mutation.error.fieldError('reason')) ? (
            <p className="text-sm text-destructive">{(mutation.error as ApiError).message}</p>
          ) : null}
        </SheetBody>
        <SheetFooter className="justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={reason.trim().length < 3 || mutation.isPending} data-testid="set-aside-confirm">
            <ThumbsUp className="size-4" aria-hidden />
            Set aside
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

function FindingGroup({
  check,
  checkId,
  findings,
  aside,
  canManage,
  onRaiseQuery,
  onAct,
  onSetAside,
  onReopen,
}: {
  check: ReviewCheck | undefined;
  checkId: string;
  findings: Finding[];
  /** Rendering the set-aside list: the note leads and "Look again" replaces the fixes. */
  aside: boolean;
  canManage: boolean;
  onRaiseQuery: (finding: Finding) => void;
  onAct: (finding: Finding, action: FindingAction) => void;
  onSetAside: (finding: Finding) => void;
  onReopen: (finding: Finding) => void;
}) {
  const currency = useBaseCurrency();
  const severity = findings[0].severity;
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border p-4">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold">{check?.title ?? checkId}</h2>
            <Badge variant={SEVERITY_BADGE[severity]}>{SEVERITY_LABEL[severity]}</Badge>
            <span className="text-xs text-muted-foreground">
              {findings.length} {findings.length === 1 ? 'record' : 'records'}
            </span>
          </div>
          {/* Written to be forwarded to a client without editing. */}
          {check ? <p className="max-w-3xl text-sm text-muted-foreground">{check.why}</p> : null}
        </div>
      </div>

      <ul className="divide-y divide-border">
        {findings.map((finding) => (
          <li key={`${finding.entityType}-${finding.entityId}-${finding.detail}`} className="flex flex-wrap items-start justify-between gap-3 p-4">
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-baseline gap-2">
                <FindingLink finding={finding} />
                {finding.date ? (
                  <span className="text-xs text-muted-foreground">{formatDate(finding.date)}</span>
                ) : null}
                {finding.amount ? (
                  <Money value={finding.amount} currency={currency} className="text-xs text-muted-foreground" />
                ) : null}
              </div>
              <p className="max-w-3xl text-sm text-muted-foreground">{finding.detail}</p>
              {finding.setAside ? (
                <p className="max-w-3xl rounded-md bg-muted/60 px-2 py-1 text-xs text-muted-foreground" data-testid="finding-set-aside-note">
                  {finding.setAside.stale ? 'Was set aside' : 'Set aside'} {formatDate(finding.setAside.at.slice(0, 10))}
                  {finding.setAside.byName ? ` by ${finding.setAside.byName}` : ''}: &ldquo;{finding.setAside.reason}&rdquo;
                  {finding.setAside.stale ? ' - the record has changed since.' : ''}
                </p>
              ) : null}
            </div>
            {/* The three verbs, in the order a person reaches for them: fix it, it's fine, ask. */}
            <div className="flex flex-wrap items-center gap-1" data-testid="finding-actions">
              {aside ? (
                canManage ? (
                  <Button size="sm" variant="outline" onClick={() => onReopen(finding)} data-testid="finding-reopen">
                    <RotateCcw className="size-4" aria-hidden />
                    Look again
                  </Button>
                ) : null
              ) : (
                <>
                  {(finding.actions ?? []).map((action) => (
                    <Button key={action.kind + (action.entityId ?? '')} size="sm" variant="outline" onClick={() => onAct(finding, action)} data-testid={`finding-action-${action.kind}`}>
                      {action.label}
                    </Button>
                  ))}
                  {canManage && finding.canSetAside !== false ? (
                    <Button size="sm" variant="ghost" onClick={() => onSetAside(finding)} data-testid="finding-set-aside">
                      <ThumbsUp className="size-4" aria-hidden />
                      It&rsquo;s fine&hellip;
                    </Button>
                  ) : null}
                  <Button size="sm" variant="ghost" onClick={() => onRaiseQuery(finding)}>
                    <MessageSquarePlus className="size-4" aria-hidden />
                    Ask the client
                  </Button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * A finding, linked to the record it is about.
 *
 * The whole value of a finding is getting to the thing it names in one click.
 * Where there is no page for the entity - a numbering gap has no record - it
 * renders as plain text rather than a link that goes nowhere.
 */
function FindingLink({ finding }: { finding: Finding }) {
  const href =
    finding.entityType === 'invoice'
      ? `/invoices/${finding.entityId}`
      : finding.entityType === 'customer'
        ? `/customers?highlight=${finding.entityId}`
        : finding.entityType === 'item'
          ? `/items?highlight=${finding.entityId}`
          : finding.entityType === 'expense'
            ? '/expenses'
            : finding.entityType === 'bankAccount' || finding.entityType === 'bankTransaction'
              ? '/banking'
              : finding.entityType === 'exportMonth'
                ? `/exports?month=${encodeURIComponent(finding.entityId)}`
                : null;

  if (!href) return <span className="font-medium">{finding.label}</span>;
  return (
    <Link to={href} className="font-medium text-primary hover:underline">
      {finding.label}
    </Link>
  );
}

/** What ran, what passed, and what did not run at all. */
function RanAndSkipped({
  result,
  checkById,
}: {
  result: ReviewResult | undefined;
  checkById: Map<string, ReviewCheck>;
}) {
  if (!result) return null;
  return (
    <Card className="p-4">
      <h2 className="text-sm font-semibold">What ran</h2>
      <div className="mt-2 space-y-2 text-xs text-muted-foreground">
        {result.passed.length ? (
          <p>
            <span className="font-medium text-success">Passed:</span>{' '}
            {result.passed.map((id) => checkById.get(id)?.title ?? id).join(' · ')}
          </p>
        ) : null}
        {result.skipped.length ? (
          <div>
            {/* Not the same as passing, and it must not read like it. */}
            <p className="font-medium text-foreground">Did not run</p>
            <ul className="mt-1 space-y-0.5">
              {result.skipped.map((entry) => (
                <li key={entry.checkId}>
                  {checkById.get(entry.checkId)?.title ?? entry.checkId} — {entry.reason}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function QueryRow({ query, orgId }: { query: ReviewQuery; orgId: string | undefined }) {
  const queryClient = useQueryClient();
  const [answer, setAnswer] = useState('');
  const [answering, setAnswering] = useState(false);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['review-queries', orgId] });
    void queryClient.invalidateQueries({ queryKey: ['practice'] });
  };

  const answerMutation = useMutation({
    mutationFn: () => api.post(`/review/queries/${query.id}/answer`, { answer }),
    onSuccess: () => {
      setAnswering(false);
      setAnswer('');
      invalidate();
    },
  });

  const resolveMutation = useMutation({
    mutationFn: () => api.post(`/review/queries/${query.id}/resolve`),
    onSuccess: invalidate,
  });

  return (
    <li className="rounded-md border border-border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <p className="text-sm">{query.question}</p>
          <p className="text-xs text-muted-foreground">
            {query.check ? `${query.check.title} · ` : ''}
            about a {query.entityType} · raised {formatDateTime(query.createdAt)}
          </p>
          {query.answer ? (
            <p className="rounded bg-muted px-2 py-1 text-xs">
              <span className="font-medium">Answer:</span> {query.answer}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Badge variant={query.status === 'answered' ? 'info' : 'warning'}>{query.status}</Badge>
          {!query.answer ? (
            <Button size="sm" variant="ghost" onClick={() => setAnswering((on) => !on)}>
              Record answer
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => resolveMutation.mutate()}
            disabled={resolveMutation.isPending}
          >
            Resolve
          </Button>
        </div>
      </div>

      {answering ? (
        <form
          className="mt-2 flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (answer.trim()) answerMutation.mutate();
          }}
        >
          <Input
            value={answer}
            onChange={(event) => setAnswer(event.target.value)}
            placeholder="What did they say?"
            aria-label="Answer"
          />
          <Button size="sm" type="submit" disabled={!answer.trim() || answerMutation.isPending}>
            Save
          </Button>
        </form>
      ) : null}

      {answerMutation.isError ? (
        <p className="mt-1 text-xs text-destructive">{(answerMutation.error as ApiError).message}</p>
      ) : null}
    </li>
  );
}

function RaiseQueryDrawer({
  finding,
  check,
  onClose,
}: {
  finding: Finding;
  check: ReviewCheck | undefined;
  onClose: () => void;
}) {
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  useEscapeToClose(onClose);

  // Pre-filled with the finding's own wording, because that is already written
  // for the client to read - and a CA who has to compose the question from
  // scratch will do it in WhatsApp instead.
  const [question, setQuestion] = useState(
    () => `${finding.label}: ${finding.detail}${check ? `\n\n${check.why}` : ''}`,
  );

  const mutation = useMutation({
    mutationFn: () =>
      api.post('/review/queries', {
        // The engine's entity types and the query schema's overlap everywhere
        // except bankAccount and series, which have no record to point at.
        entityType: (['invoice', 'customer', 'expense', 'bankTransaction', 'item'] as string[]).includes(
          finding.entityType,
        )
          ? finding.entityType
          : 'general',
        entityId: (['invoice', 'customer', 'expense', 'bankTransaction', 'item'] as string[]).includes(
          finding.entityType,
        )
          ? finding.entityId
          : undefined,
        checkId: finding.checkId,
        question: question.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['review-queries', org?.id] });
      onClose();
    },
  });

  return (
    <Sheet label="Ask the client" onClose={onClose}>
      <SheetHeader title="Ask the client" onClose={onClose} />
      <SheetForm
        onSubmit={(event) => {
          event.preventDefault();
          if (question.trim()) mutation.mutate();
        }}
      >
        <SheetBody>
          <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">{check?.title ?? finding.checkId}</p>
            <p className="mt-1">
              {finding.label}
              {finding.date ? ` · ${formatDate(finding.date)}` : ''}
            </p>
          </div>

          <div className="space-y-1">
            <label htmlFor="question" className="text-sm font-medium">
              Question
            </label>
            <Textarea
              id="question"
              rows={8}
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              aria-describedby="question-help"
            />
            <p id="question-help" className="text-xs text-muted-foreground">
              This is what the client reads. It is pre-filled from the finding - edit it however you
              like.
            </p>
          </div>

          {mutation.isError ? (
            <p className="text-sm text-destructive">{(mutation.error as ApiError).message}</p>
          ) : null}
        </SheetBody>
        <SheetFooter className="justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!question.trim() || mutation.isPending}>
            <ClipboardCheck className="size-4" aria-hidden />
            Raise query
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}
